import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, message, Tag, Descriptions, Alert, Spin } from 'antd';
import ResponsiveTable from '../../components/common/ResponsiveTable';
import ResponsiveTabs from '../../components/common/ResponsiveTabs';
import { SendOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { changePassword } from '../../api/auth';
import { getMyConsents, getStudentKvkkConsents, decideStudentKvkkConsent } from '../../api/kvkk';
import useAuthStore from '../../store/authStore';
import { formatPhone } from '../../utils/phone';
import api from '../../api/axios';
import dayjs from 'dayjs';
import KvkkContentModal from '../../components/common/KvkkContentModal';
import NotificationsHistoryTab from '../../components/common/NotificationsHistoryTab';
import { apiErrorMessage } from '../../utils/apiError';

const STUDENT_CONSENT_STATUS_TAGS = {
  active: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
  pending: <Tag color="gold">Bekliyor</Tag>,
};

const ChildConsentsCard = () => {
  const [children, setChildren] = useState([]);
  const [consentsByChild, setConsentsByChild] = useState({});
  const [loading, setLoading] = useState(true);
  const [decidingKey, setDecidingKey] = useState(null);
  const [modalData, setModalData] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const dashRes = await api.get('/parent/dashboard');
      const kids = dashRes.data.children || [];
      setChildren(kids);

      const entries = await Promise.all(
        kids.map(async (kid) => {
          try {
            const res = await getStudentKvkkConsents(kid.id);
            return [kid.id, res.data.data || []];
          } catch {
            return [kid.id, []];
          }
        })
      );
      setConsentsByChild(Object.fromEntries(entries));
    } catch {
      message.error('Öğrenci bilgileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDecide = async (studentId, type, decision) => {
    const key = `${studentId}-${type}`;
    setDecidingKey(key);
    try {
      await decideStudentKvkkConsent(studentId, type, decision);
      message.success(decision === 'accept' ? 'Onaylandı.' : 'Reddedildi.');
      fetchAll();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setDecidingKey(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Spin /></div>;

  const hasAnyConsents = children.some(kid => (consentsByChild[kid.id] || []).length > 0);
  if (!hasAnyConsents) return null;

  return (
    <>
      <Card title="Çocuklarım İçin Onay Metinleri" className="mb-4">
        <div className="space-y-4">
          {children.map(kid => {
            const items = consentsByChild[kid.id] || [];
            if (items.length === 0) return null;
            return (
              <div key={kid.id} className="border rounded p-3">
                <div className="font-semibold mb-2">{kid.name} {kid.surname}</div>
                {items.map(item => {
                  const key = `${kid.id}-${item.type}`;
                  return (
                    <div key={item.type} className="flex items-center justify-between flex-wrap gap-2 py-2 border-t first:border-t-0">
                      <div className="flex items-center gap-2">
                        <span>{item.label}</span>
                        {STUDENT_CONSENT_STATUS_TAGS[item.status]}
                        <Button size="small" onClick={() => setModalData(item)}>Metni Görüntüle</Button>
                        {item.responded_by && (
                          <span className="text-xs text-gray-400">
                            {item.responded_by} — {dayjs(item.responded_at).format('DD.MM.YYYY HH:mm')}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {item.status !== 'active' && (
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={decidingKey === key}
                            onClick={() => handleDecide(kid.id, item.type, 'accept')}
                          >
                            Onayla
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </Card>
      <KvkkContentModal
        open={!!modalData}
        onClose={() => setModalData(null)}
        content={modalData?.text?.content}
        version={modalData?.text?.version}
        label={modalData?.label}
      />
    </>
  );
};

const KvkkConsentsTab = () => {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    getMyConsents()
      .then(res => setConsents(res.data.data))
      .catch(() => message.error('Onaylar yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Tür', render: (_, r) => r.kvkk_text?.text_type?.label || r.kvkk_text?.type },
    { title: 'Versiyon', render: (_, r) => `v${r.kvkk_text?.version}` },
    { title: 'Onay Tarihi', render: (_, r) => dayjs(r.accepted_at).format('DD.MM.YYYY HH:mm') },
    {
      title: 'Durum',
      render: (_, r) => r.status === 'active'
        ? <Tag color="green">Aktif</Tag>
        : <Tag color="red">İptal edildi</Tag>,
    },
    {
      title: 'Onaylanan Metin',
      render: (_, r) => (
        <Button size="small" onClick={() => setModalData(r.kvkk_text)}>Görüntüle</Button>
      ),
    },
  ];

  return (
    <>
      <ChildConsentsCard />
      <Card title="Onay Geçmişim">
        <ResponsiveTable rowKey="id" columns={columns} dataSource={consents} loading={loading} pagination={false} scroll={{ x: 'max-content' }} />
      </Card>
      <KvkkContentModal
        open={!!modalData}
        onClose={() => setModalData(null)}
        content={modalData?.content}
        version={modalData?.version}
        type={modalData?.type}
      />
    </>
  );
};

const ParentProfile = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'info';
  const { user } = useAuthStore();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const [requestForm] = Form.useForm();

  const handlePasswordChange = async (values) => {
    setPasswordLoading(true);
    try {
      await changePassword(values.current_password, values.password, values.password_confirmation);
      message.success('Şifre başarıyla güncellendi.');
      passwordForm.resetFields();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Şifre güncellenemedi.'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileChangeRequest = async (values) => {
    setRequestLoading(true);
    try {
      await api.post('/parent/request-profile-change', { message: values.message });
      message.success('İsteğiniz şube müdürü ve yönetime iletildi.');
      requestForm.resetFields();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İstek gönderilemedi.'));
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Profilim</h2>

      <ResponsiveTabs
        defaultActiveKey={defaultTab}
        items={[
          {
            key: 'info',
            label: 'Profil Bilgileri',
            children: (
              <>
                <Card className="mb-4">
                  <Descriptions column={1} bordered size="middle">
                    <Descriptions.Item label="Ad Soyad">{user?.name} {user?.surname}</Descriptions.Item>
                    <Descriptions.Item label="E-posta">{user?.email}</Descriptions.Item>
                    <Descriptions.Item label="Telefon">{formatPhone(user?.phone) || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card title="Bilgi Değişikliği Talebi">
                  <Alert
                    message="Profil bilgilerinizi değiştirmek için aşağıdaki formu doldurun. Talebiniz şube müdürü ve yönetime iletilecektir."
                    type="info"
                    showIcon
                    className="mb-4"
                  />
                  <Form
                    form={requestForm}
                    layout="vertical"
                    onFinish={handleProfileChangeRequest}
                    style={{ maxWidth: 500 }}
                  >
                    <Form.Item
                      name="message"
                      label="Değişiklik Talebi"
                      rules={[{ required: true, message: 'Lütfen talebinizi yazınız.' }]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Örn: İsim düzeltmesi: Ahmet → Ahmet Ali, Telefon: 0532 xxx xx xx olarak güncellensin"
                        maxLength={1000}
                        showCount
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={requestLoading}
                        icon={<SendOutlined />}
                      >
                        İstek Gönder
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </>
            ),
          },
          {
            key: 'password',
            label: 'Şifre Değiştir',
            children: (
              <Card>
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordChange}
                  style={{ maxWidth: 400 }}
                >
                  <Form.Item name="current_password" label="Mevcut Şifre" rules={[{ required: true }]}>
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="Yeni Şifre"
                    rules={[{ required: true }, { min: 8, message: 'En az 8 karakter.' }]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    name="password_confirmation"
                    label="Yeni Şifre Tekrar"
                    dependencies={['password']}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) return Promise.resolve();
                          return Promise.reject(new Error('Şifreler eşleşmiyor.'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={passwordLoading}>
                      Şifreyi Güncelle
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            ),
          },
          {
            key: 'notifications',
            label: 'Bildirimlerim',
            children: <NotificationsHistoryTab />,
          },
          {
            key: 'kvkk',
            label: 'Onaylarım',
            children: <KvkkConsentsTab />,
          },
        ]}
      />
    </div>
  );
};

export default ParentProfile;
